import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, LogOut, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const STAGES = [
  "Manager Approval", "HR Discussion", "Knowledge Transfer",
  "Asset Return", "Finance Clearance", "Exit Interview", "FNF Settlement",
];

export default function MyResignation() {
  const { user } = useAuth();
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ last_working_day: "", reason: "", notes: "" });

  useEffect(() => {
    api.get("/my-exit")
      .then((r) => setActive(r.data || null))
      .catch(() => setActive(null))
      .finally(() => setLoading(false));
  }, []);

  const submit = async () => {
    if (!form.last_working_day) { toast.error("Please choose your last working day"); return; }
    if (!form.reason || form.reason.trim().length < 3) { toast.error("Please share a brief reason"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/exits/self-initiate", form);
      toast.success("Resignation submitted. HR has been notified.");
      setActive(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>;
  }

  return (
    <div data-testid="my-resign-page">
      <PageHeader
        title="Submit Resignation"
        subtitle="Initiate your exit formalities. HR and your manager will be notified."
      />

      {active ? (
        <Card className="hr-card p-6 max-w-2xl">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-6 w-6 text-emerald-600 mt-0.5" />
            <div>
              <div className="font-display text-lg font-semibold text-[#0F172A]">Resignation in progress</div>
              <p className="text-xs text-gray-500 mt-0.5">
                Submitted on {active.resignation_date} · Last working day {active.last_working_day}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-5 text-sm">
            <Info label="Status" value={active.status} />
            <Info label="Current stage" value={active.stage} />
            <Info label="Reason" value={active.reason} />
            <Info label="Exit type" value={active.exit_type} />
          </div>

          <div className="mt-6">
            <div className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">7-step clearance progress</div>
            <div className="flex flex-wrap gap-2">
              {STAGES.map((s, i) => {
                const done = i < (active.stage_index || 0);
                const current = i === (active.stage_index || 0);
                return (
                  <div
                    key={s}
                    className={`text-[11px] px-2 py-1 rounded-full border ${
                      done ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                      : current ? "bg-[#F26B3A]/10 border-[#F26B3A]/30 text-[#F26B3A]"
                      : "bg-gray-50 border-gray-200 text-gray-500"
                    }`}
                  >
                    {i + 1}. {s}
                  </div>
                );
              })}
            </div>
          </div>

          {active.notes && (
            <div className="mt-5 text-xs">
              <div className="text-[11px] uppercase tracking-wider text-gray-400 mb-1">Notes</div>
              <div className="text-gray-700 whitespace-pre-wrap">{active.notes}</div>
            </div>
          )}
        </Card>
      ) : (
        <Card className="hr-card p-6 max-w-2xl" data-testid="resign-form">
          <div className="flex items-center gap-2 mb-4">
            <LogOut className="h-5 w-5 text-[#F26B3A]" />
            <div className="font-display text-lg font-semibold text-[#0F172A]">Submit your resignation</div>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Last working day</Label>
              <Input
                type="date"
                data-testid="resign-last-working-day"
                value={form.last_working_day}
                onChange={(e) => setForm({ ...form, last_working_day: e.target.value })}
                className="mt-1.5"
              />
              <p className="text-[11px] text-gray-500 mt-1">Please factor in your notice period as per your appointment letter.</p>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Reason</Label>
              <Input
                data-testid="resign-reason"
                placeholder="e.g. Personal reasons, Higher studies, Better opportunity"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Additional notes (optional)</Label>
              <Textarea
                rows={4}
                data-testid="resign-notes"
                placeholder="Anything you'd like HR and your manager to know."
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1.5"
              />
            </div>

            <div className="text-[11px] text-gray-500 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              Submitting will trigger the 7-step clearance workflow and notify HR. This action will be logged against your account ({user?.email}).
            </div>

            <Button
              data-testid="resign-submit"
              onClick={submit}
              disabled={submitting}
              className="bg-[#F26B3A] hover:bg-[#d85d2f]"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit resignation"}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-gray-400">{label}</div>
      <div className="text-gray-800 capitalize">{value || "—"}</div>
    </div>
  );
}
